import { useId, type ReactNode } from "react";

import { tone, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";

const THUMB = "1rem";

export type SliderTrackProps = {
  value: number;
  min: number;
  max: number;
  step?: number | undefined;
  id?: string | undefined;

  label?: string | undefined;

  valueText?: string | undefined;
  className?: string | undefined;
  tone?: Tone | undefined;
  onChange: (value: number) => void;
};

export function SliderTrack({
  value,
  min,
  max,
  step,
  id,
  label,
  valueText,
  className,
  tone: which,
  onChange,
}: SliderTrackProps) {
  const styles = tone(which);
  const span = max - min;
  const fraction = span <= 0 ? 0 : Math.min(1, Math.max(0, (value - min) / span));

  return (
    <div className={cn("relative flex h-4 min-w-0 items-center", className)}>
      <span aria-hidden className={cn("absolute inset-x-0 h-2 rounded-full", styles.switchOff)} />
      <span
        aria-hidden
        className="absolute h-2 rounded-full bg-brand"
        style={{
          insetInlineStart: 0,
          width: `calc(${THUMB} / 2 + (100% - ${THUMB}) * ${fraction})`,
        }}
      />
      <input
        aria-label={label}
        aria-valuetext={valueText}
        className="relative h-4 w-full min-w-0 cursor-pointer appearance-none bg-transparent"
        id={id}
        max={max}
        min={min}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
        step={step}
        type="range"
        value={value}
      />
    </div>
  );
}

export type SliderProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number | undefined;

  readout: string;

  minLabel: string;
  maxLabel: string;
  icon?: ReactNode;

  trailing?: ReactNode;
  tone?: Tone | undefined;
  onChange: (value: number) => void;
};

export function Slider({
  label,
  value,
  min,
  max,
  step,
  readout,
  minLabel,
  maxLabel,
  icon,
  trailing,
  tone: which,
  onChange,
}: SliderProps) {
  const styles = tone(which);
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label className={cn("text-sm font-medium", styles.text)} htmlFor={id}>
          {label}
        </label>
        <span className={cn("text-xs tabular-nums", styles.muted)}>{readout}</span>
      </div>
      <div className="flex items-center gap-3">
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <SliderTrack
            id={id}
            max={max}
            min={min}
            onChange={onChange}
            step={step}
            tone={which}
            value={value}
            valueText={readout}
          />
          {}
          <div
            aria-hidden
            className={cn("flex items-baseline justify-between gap-2 text-xs", styles.muted)}
          >
            <span>{minLabel}</span>
            <span>{maxLabel}</span>
          </div>
        </div>
        {trailing}
      </div>
    </div>
  );
}
